// ============================================================
// FileUpload.tsx
// Self-contained file uploader supporting two modes:
//   - caseId-mode: case-attached upload via the case-scoped
//     endpoints (POST /api/cases/:caseId/files/upload-url)
//   - clientId-mode: client-attached upload via the flat
//     endpoints (POST /api/files/upload-url)
// Exactly one of caseId/clientId must be provided.
//
// Upload sequence (both modes):
//   1. Validate file (filename non-empty, ≤1 GB)
//   2. Request presigned URL — backend creates FileAttachment
//      row atomically
//   3. PUT file bytes to R2 directly via plain fetch (NOT axios
//      — Bearer interceptor would invalidate the signature)
//   4. onUploadComplete fires after a 2-second success display
//
// Failure handling: if step 3 fails AFTER step 2 created the
// row, the catch block calls the matching delete endpoint to
// remove the orphan. Cleanup errors are swallowed; the original
// upload error is what surfaces to the user.
// ============================================================
//
// Stage 4 (case-sections batch 3, casevo-case-sections-batch3.html .drop):
// restyled as the full-width drop zone. Upload-status wiring (schema batch,
// backend lib/fileStatus.ts): after the PUT succeeds the row is CONFIRMED via
// POST /api/files/:id/complete (pending → ready), best-effort — list
// endpoints self-heal recent pending rows anyway. While a file is in flight
// the parent is told (onPendingChange) so the list can show the pending row
// ("Hidden from others until confirmed") with a Cancel that aborts the PUT
// and runs the SAME orphan cleanup as a failed upload.
// ============================================================
import React, { useState, useRef, useEffect } from 'react'
import { requestUploadUrlApi, requestUploadUrlFlatApi, deleteFileApi, deleteFileFlatApi, completeFileUploadApi } from '../../api/casevoApi'

// The in-flight upload, for the parent's pending row.
export interface PendingUpload {
  filename: string
  mimeType: string
  sizeBytes: number
  // 'uploading' = PUT in flight; 'confirming' = PUT done, confirming ready;
  // 'uploaded' = confirmed, waiting for the list refresh.
  phase: 'uploading' | 'confirming' | 'uploaded'
  cancel: () => void
}

interface FileUploadProps {
  // EXACTLY ONE of caseId or clientId must be provided (XOR).
  // caseId-mode uses the case-scoped endpoints (POST /api/cases/:caseId/files/upload-url + DELETE /api/cases/:caseId/files/:fileId).
  // clientId-mode uses the flat endpoints (POST /api/files/upload-url + DELETE /api/files/:fileId).
  // The component dev-asserts at mount and runtime-checks in handleFile.
  caseId?: string
  clientId?: string
  onUploadComplete: () => void
  // Optional: the in-flight upload (null when idle) for a pending row.
  onPendingChange?: (pending: PendingUpload | null) => void
}

const UploadIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

// Mode drives both the zone's visual state and its interactivity.
// 'uploading' blocks click/drop; the other three accept new files.
type UploadMode = 'idle' | 'uploading' | 'success' | 'error'

// Matches the backend's storage cap on POST /upload-url (1 GiB) — large
// evidence media (dashcam/CCTV/medical footage) is storable.
const MAX_BYTES = 1024 * 1024 * 1024

const FileUpload: React.FC<FileUploadProps> = ({ caseId, clientId, onUploadComplete, onPendingChange }) => {
  const [mode, setMode] = useState<UploadMode>('idle')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [currentFilename, setCurrentFilename] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  // Held so the success → idle reset can be cancelled if the user
  // picks another file mid-fade, or if the parent unmounts us.
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Abort handle for the in-flight R2 PUT (the pending row's Cancel).
  const abortRef = useRef<AbortController | null>(null)
  const pendingCbRef = useRef(onPendingChange)
  pendingCbRef.current = onPendingChange

  // Dev-only assertion: exactly one of caseId/clientId must be set.
  // Catches misconfigurations loudly during development. The runtime
  // safety net inside handleFile is the production-build fallback.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const hasCase = Boolean(caseId)
      const hasClient = Boolean(clientId)
      if (hasCase === hasClient) {
        // Both true OR both false. Either way, invalid.
        // eslint-disable-next-line no-console
        console.error(
          '[FileUpload] Exactly one of caseId or clientId must be provided. Got:',
          { caseId, clientId },
        )
      }
    }
  }, [caseId, clientId])

  // Cancel any pending success-reset on unmount — avoids the
  // setState-on-unmounted React warning.
  useEffect(() => {
    return () => {
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current)
        successTimerRef.current = null
      }
    }
  }, [])

  // Validate against the same rules the backend enforces, so the
  // user sees the same error inline rather than waiting for a 400.
  const validate = (file: File): string | null => {
    if (!file.name.trim()) return 'Filename cannot be empty'
    if (file.size <= 0 || file.size > MAX_BYTES) {
      return 'File too large (max 1 GB)'
    }
    return null
  }

  const handleFile = async (file: File) => {
    // Runtime safety net for the XOR invariant. The dev-mount
    // assertion above is the louder developer-facing check; this is
    // the silent production-build safety so we never fire a malformed
    // request. Logged + early-returned BEFORE any UI changes so an
    // invalid config doesn't leave the drop zone stuck on "Uploading…".
    const hasCase = Boolean(caseId)
    const hasClient = Boolean(clientId)
    if (hasCase === hasClient) {
      // eslint-disable-next-line no-console
      console.error('[FileUpload] handleFile invoked with invalid XOR — aborting')
      return
    }
    // Cancel any pending success-reset timer — selecting another
    // file mid-fade jumps straight to the new upload.
    if (successTimerRef.current !== null) {
      clearTimeout(successTimerRef.current)
      successTimerRef.current = null
    }
    // Clear stale displays from a prior cycle before re-evaluating.
    setMode('idle')
    setValidationError(null)
    setErrorMessage(null)
    setCurrentFilename('')

    const err = validate(file)
    if (err) {
      setValidationError(err)
      return
    }

    setMode('uploading')
    setCurrentFilename(file.name)
    const abort = new AbortController()
    abortRef.current = abort
    const report = (phase: PendingUpload['phase'] | null): void => {
      pendingCbRef.current?.(phase === null ? null : {
        filename: file.name, mimeType: file.type, sizeBytes: file.size, phase,
        cancel: () => abort.abort(),
      })
    }
    report('uploading')

    // fileId is only set after the backend round-trip (step 2). If
    // the upload PUT (step 3) fails AFTER that, we have an orphan
    // row to clean up; if it fails BEFORE, nothing to clean.
    let fileId: string | null = null
    try {
      // Step 2 — provision the upload URL. Branches on which prop is
      // set; the runtime safety net at the top of handleFile already
      // validated exactly one of caseId/clientId is truthy, so the
      // `clientId!` non-null assertion in the else branch is safe.
      const provisioned = caseId
        ? await requestUploadUrlApi(caseId, {
            filename: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
          })
        : await requestUploadUrlFlatApi({
            clientId: clientId!,
            filename: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
          })
      fileId = provisioned.fileId
      // Cancelled while the URL was being provisioned → straight to cleanup.
      if (abort.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError')

      // Step 3 — PUT directly to R2. ONLY Content-Type is sent.
      // Adding Authorization (or anything else) would invalidate
      // the presigned signature. fetch is used so the app's axios
      // request interceptor doesn't sneak a Bearer token in.
      const r2Response = await fetch(provisioned.uploadUrl, {
        method: 'PUT',
        // The type the server resolved (it fills in .msg / .eml the browser left blank).
        headers: { 'Content-Type': provisioned.mimeType ?? file.type },
        body: file,
        signal: abort.signal,
      })
      if (!r2Response.ok) {
        throw new Error(`Upload failed (status ${r2Response.status})`)
      }

      // Step 3b — confirm the upload (pending → ready). Best-effort: a
      // failure here leaves the row pending, and the list endpoint
      // self-heals recent pending rows (HEAD-checks R2) on the next load.
      report('confirming')
      await completeFileUploadApi(provisioned.fileId).catch(() => undefined)

      // Step 4a — success.
      abortRef.current = null
      report('uploaded')
      setMode('success')
      successTimerRef.current = setTimeout(() => {
        successTimerRef.current = null
        setMode('idle')
        setCurrentFilename('')
        report(null)
        onUploadComplete()
      }, 2000)
    } catch (uploadErr) {
      // Step 4b — failure. If the row was already provisioned (PUT
      // failed AFTER backend committed), wipe the orphan via the
      // matching endpoint. Branches on caseId: caseId-mode uses the
      // case-scoped deleteFileApi; clientId-mode uses deleteFileFlatApi
      // (no case in the URL). Swallow any cleanup error — best-effort,
      // we already have the real upload error to surface.
      if (fileId !== null) {
        try {
          if (caseId) {
            await deleteFileApi(caseId, fileId)
          } else {
            await deleteFileFlatApi(fileId)
          }
        } catch {
          /* intentional swallow — orphan cleanup is best-effort */
        }
      }
      abortRef.current = null
      report(null)
      const cancelled = abort.signal.aborted
      const message = cancelled
        ? 'Upload cancelled.'
        : uploadErr instanceof Error && uploadErr.message
          ? uploadErr.message
          : 'Upload failed. Please try again.'
      setErrorMessage(message)
      setMode('error')
    }
  }

  const openFilePicker = () => {
    if (mode === 'uploading') return
    fileInputRef.current?.click()
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input value so picking the SAME file twice still
    // fires onChange (browsers suppress change when value matches).
    e.target.value = ''
    if (file) void handleFile(file)
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode === 'uploading') return
    // preventDefault is what tells the browser "this element accepts
    // drops" — without it, onDrop never fires.
    e.preventDefault()
    setIsDragging(true)
  }

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    // Moving between the zone's own children fires dragleave too — only
    // clear the highlight when the pointer really leaves the zone.
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
    setIsDragging(false)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    if (mode === 'uploading') return
    const file = e.dataTransfer.files?.[0]
    if (file) void handleFile(file)
  }

  // Drop zone (mockup .drop / .drop.over, batch 3).
  const busy = mode === 'uploading'
  const zoneClass = [
    'flex items-center gap-4 px-5 py-[18px] rounded-[14px] border-[1.5px] transition-[border-color,background-color,box-shadow] text-casevo-forest',
    busy ? 'cursor-progress' : 'cursor-pointer',
    isDragging
      ? 'border-solid border-casevo-brand bg-[color-mix(in_srgb,var(--casevo-mint)_55%,transparent)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--casevo-mid)_16%,transparent)]'
      : 'border-dashed border-[color-mix(in_srgb,var(--casevo-brand)_40%,var(--casevo-border))] bg-[color-mix(in_srgb,var(--casevo-mint)_22%,transparent)] hover:border-casevo-mid',
  ].join(' ')
  const title = isDragging
    ? (caseId ? 'Drop to upload to this case' : 'Drop to upload')
    : mode === 'uploading'
      ? `Uploading ${currentFilename}…`
      : mode === 'success'
        ? `Uploaded ✓ ${currentFilename}`
        : 'Drag files here, or choose a file'

  return (
    <div>
      {/* Hidden native input — opened programmatically by zone click. */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={onInputChange}
        aria-hidden="true"
        tabIndex={-1}
      />
      <div
        className={zoneClass}
        onClick={openFilePicker}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        aria-busy={busy || undefined}
      >
        <span className="w-11 h-11 rounded-[12px] bg-casevo-surface border border-casevo-border grid place-items-center text-casevo-brand dark:text-[#a8e6c5] shrink-0">
          {busy
            ? <span className="w-[18px] h-[18px] rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin motion-reduce:animate-none" aria-hidden="true" />
            : <UploadIcon />}
        </span>
        <div className="min-w-0 flex-1" aria-live="polite">
          <b className={`block text-[14.5px] font-bold truncate ${mode === 'success' ? 'text-casevo-brand dark:text-[#8fd6b0]' : ''}`}>{title}</b>
          <div className="text-[12.5px] text-casevo-text-soft mt-[2px]">
            PDFs, images, Office documents and evidence video, up to 1&nbsp;GB each. Files upload directly to secure storage.
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); openFilePicker() }}
          disabled={busy}
          className="ml-auto shrink-0 inline-flex items-center gap-[7px] h-9 px-[14px] rounded-[10px] text-[13.5px] font-medium border border-casevo-border bg-casevo-surface text-casevo-forest whitespace-nowrap transition-colors enabled:hover:border-casevo-mid disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus"
        >
          <UploadIcon size={15} />Choose file
        </button>
      </div>
      {validationError && (
        <p role="alert" className="text-casevo-danger text-[12.5px] mt-2">{validationError}</p>
      )}
      {mode === 'error' && errorMessage && (
        <p role="alert" className="text-casevo-danger text-[12.5px] mt-2">{errorMessage}</p>
      )}
    </div>
  )
}

export default FileUpload
